import { useState, useMemo, useCallback } from 'react';
import { useAutoSaveParams } from '@sg/hooks/useAutoSaveParams';
import moment from 'moment';

// Import PlainTextFile type from parent component
type PlainTextFile = {
    name: string;
    path: string;
    format?: string;
    size?: number | string;
    [key: string]: any;
};

interface UsePlainTextAutoSaveProps {
    autoSavePageKey?: string;
    autoSaveId?: string | number;
    data: string;
    time: any;
    editData: string;
    isEditing: boolean;
    // Route prompt props
    editFiles: PlainTextFile[];
    originalFiles: PlainTextFile[];
}

/**
 * Custom hook to manage auto-save and route prompt functionality for PlainText component
 * Handles auto-save registration, recovery prompts, cleanup, and unsaved changes detection
 */
export const usePlainTextAutoSave = ({
    autoSavePageKey,
    autoSaveId,
    data,
    time,
    editData,
    isEditing,
    editFiles,
    originalFiles,
}: UsePlainTextAutoSaveProps) => {
    const [autoSaveVisible, setAutoSaveVisible] = useState(false);

    // Auto-save: construct unique pageKey
    const pageKeyCur = useMemo(() => {
        if (!autoSavePageKey) return '';
        return autoSaveId !== undefined
            ? `${autoSavePageKey}_${autoSaveId}`
            : autoSavePageKey;
    }, [autoSavePageKey, autoSaveId]);

    // Auto-save: get methods (check if auto-save exists)
    const { getSaveParamsValue, showRestorePrompt, deleteSaveParamsByPageKey } = useAutoSaveParams({
        pageKey: pageKeyCur,
    });

    // Auto-save: start auto-saving when in edit mode
    // Include both note, date, and files in the auto-save
    useAutoSaveParams({
        pageKey: pageKeyCur,
        initialParams: {
            note: data,
            date: time ? time.format('YYYY-MM-DD') : null,
            files: originalFiles,
        },
        saveParamsCur: {
            note: editData,
            date: time ? time.format('YYYY-MM-DD') : null,
            files: editFiles,
        },
        isBegin: isEditing && !!autoSavePageKey,
        isChangeValue: true,
    });

    // Check for auto-save before entering edit mode
    const checkAutoSaveBeforeEdit = useCallback(() => {
        if (autoSavePageKey && showRestorePrompt(pageKeyCur)) {
            setAutoSaveVisible(true);
            return true; // Has auto-save
        }
        return false; // No auto-save
    }, [autoSavePageKey, showRestorePrompt, pageKeyCur]);

    // Get auto-saved values (including files)
    const getAutoSavedValues = useCallback(() => {
        const saveValue = getSaveParamsValue(pageKeyCur) || {};
        return {
            note: saveValue.note || data,
            date: saveValue.date ? moment(saveValue.date) : null,
            files: saveValue.files || originalFiles,
        };
    }, [getSaveParamsValue, pageKeyCur, data, originalFiles]);

    // Clear auto-save data
    const clearAutoSave = useCallback(() => {
        if (autoSavePageKey) {
            deleteSaveParamsByPageKey(pageKeyCur);
        }
    }, [autoSavePageKey, deleteSaveParamsByPageKey, pageKeyCur]);

    // Route prompt: Determine if there are unsaved changes
    const hasUnsavedChanges = useMemo(() => {
        if (!isEditing) return false;

        // Check if text content has changed
        const hasTextChanges = editData !== data;

        // Check if files have changed
        const hasFileChanges = JSON.stringify(editFiles) !== JSON.stringify(originalFiles);

        return hasTextChanges || hasFileChanges;
    }, [isEditing, editData, data, editFiles, originalFiles]);

    return {
        autoSaveVisible,
        setAutoSaveVisible,
        pageKeyCur,
        checkAutoSaveBeforeEdit,
        getAutoSavedValues,
        clearAutoSave,
        hasUnsavedChanges,
    };
};
